import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { Building2, RefreshCw, ShieldAlert } from "lucide-react";

import { useBusinessAuth } from "../../business/context/BusinessAuthContext";
import { useRequiredTracking } from "../tracking/RequiredTrackingProvider";
import useRiderWork from "../context/useRiderWork";
import {
  clearRiderBusinessId,
  readRiderBusinessId,
  resolveExplicitRiderBusinessId,
  writeRiderBusinessId,
} from "../context/riderBusinessContext";
import { RiderPortalContext } from "../context/RiderPortalContext";

function CenteredState({ icon: Icon = RefreshCw, title, children }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-stone-100 px-4 text-stone-950 dark:bg-neutral-950 dark:text-white">
      <section className="w-full max-w-md rounded-3xl border border-stone-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-neutral-900">
        <Icon className="mb-4 h-8 w-8 text-emerald-600" aria-hidden="true" />
        <h1 className="text-xl font-semibold">{title}</h1>
        <div className="mt-3 text-sm leading-6 text-stone-600 dark:text-stone-300">{children}</div>
      </section>
    </main>
  );
}

export default function RiderPortalGuard() {
  const auth = useBusinessAuth();
  const location = useLocation();
  const memberships = useMemo(() => auth.memberships || [], [auth.memberships]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(() => location.state?.riderBusinessId || readRiderBusinessId());

  useEffect(() => {
    if (location.state?.riderBusinessId) setSelectedBusinessId(location.state.riderBusinessId);
  }, [location.key, location.state?.riderBusinessId]);

  const explicitBusinessId = useMemo(
    () => resolveExplicitRiderBusinessId(selectedBusinessId, memberships),
    [memberships, selectedBusinessId],
  );

  useEffect(() => {
    if (explicitBusinessId) writeRiderBusinessId(explicitBusinessId);
  }, [explicitBusinessId]);

  const work = useRiderWork(explicitBusinessId, !auth.loading && auth.isAuthenticated, auth.token);
  const { context, status, refreshContext: loadContext } = work;
  const tracking = useRequiredTracking();
  const registerTracking = tracking?.register;
  useEffect(() => {
    if (status === "ready" && !work.stale && work.currentRun !== undefined) registerTracking?.(explicitBusinessId, work.currentRun);
  }, [explicitBusinessId, work.currentRun, work.stale, status, registerTracking]);

  const chooseBusiness = useCallback(async (businessId) => {
    const explicit = resolveExplicitRiderBusinessId(businessId, memberships);
    if (!explicit) return;
    writeRiderBusinessId(explicit);
    setSelectedBusinessId(explicit);
    if (typeof auth.selectBusiness === "function") await auth.selectBusiness(explicit);
  }, [auth, memberships]);

  const clearBusiness = useCallback(() => {
    clearRiderBusinessId();
    setSelectedBusinessId(null);
  }, []);

  const returnToBusiness = async () => {
    if (typeof auth.selectBusiness === "function") await auth.selectBusiness(explicitBusinessId);
  };
  const providerValue = { ...work, businessId: explicitBusinessId, clearBusiness, returnToBusiness };

  if (auth.loading) {
    return <CenteredState title="Opening rider workspace">Loading your account…</CenteredState>;
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/business/login" replace state={{ from: location.pathname }} />;
  }

  if (!explicitBusinessId) {
    const onlyWorkplace = memberships.length === 1 ? memberships[0] : null;

    return (
      <CenteredState icon={Building2} title="Choose your workplace">
        <p>Select the store you're delivering for today.</p>
        <Link to="/business" className="mt-3 inline-block font-semibold text-emerald-700 underline dark:text-emerald-300">Back to business</Link>
        <div className="mt-5 space-y-3">
          {onlyWorkplace ? (
            <button
              type="button"
              onClick={() => chooseBusiness(onlyWorkplace.id)}
              className="flex min-h-12 w-full items-center justify-between rounded-2xl border border-stone-200 px-4 py-3 text-left font-medium transition hover:border-emerald-500 hover:bg-emerald-50 dark:border-white/10 dark:hover:bg-emerald-500/10"
            >
              <span>Continue with {onlyWorkplace.name}</span>
            </button>
          ) : memberships.map((membership) => (
            <button
              key={membership.id}
              type="button"
              onClick={() => chooseBusiness(membership.id)}
              className="flex min-h-12 w-full items-center justify-between rounded-2xl border border-stone-200 px-4 py-3 text-left font-medium transition hover:border-emerald-500 hover:bg-emerald-50 dark:border-white/10 dark:hover:bg-emerald-500/10"
            >
              <span>{membership.name}</span>
              <span className="text-xs font-normal text-stone-500 dark:text-stone-400">Select</span>
            </button>
          ))}
          {memberships.length === 0 && (
            <p className="rounded-2xl bg-stone-100 p-4 dark:bg-white/5">No workplaces are available for this account.</p>
          )}
        </div>
      </CenteredState>
    );
  }

  if (status === "loading" || status === "idle") {
    return <CenteredState title="Checking access">Making sure rider access is available for this store…</CenteredState>;
  }

  if (status === "unauthorized") {
    return <Navigate to="/business/login" replace state={{ from: location.pathname }} />;
  }

  if (status === "ineligible") {
    return (
      <CenteredState icon={ShieldAlert} title="Rider access unavailable">
        <p>Rider access is not currently available for this store. Contact your store manager if you need help.</p>
        <div className="mt-5 flex gap-3">
          <button type="button" onClick={clearBusiness} className="min-h-11 flex-1 rounded-xl bg-stone-900 px-4 py-2 font-medium text-white dark:bg-white dark:text-stone-950">
            {memberships.length > 1 ? "Choose another workplace" : "Back"}
          </button>
          <button type="button" onClick={loadContext} className="min-h-11 rounded-xl border border-stone-300 px-4 py-2 font-medium dark:border-white/20">
            Retry
          </button>
        </div>
      </CenteredState>
    );
  }

  if (status === "error" && !context) {
    return (
      <CenteredState icon={ShieldAlert} title="Couldn't open rider workspace">
        <p>Please try again. If the problem continues, contact your store manager.</p>
        <div className="mt-5 flex gap-3">
          <button type="button" onClick={clearBusiness} className="min-h-11 flex-1 rounded-xl bg-stone-900 px-4 py-2 font-medium text-white dark:bg-white dark:text-stone-950">
            {memberships.length > 1 ? "Choose another workplace" : "Back"}
          </button>
          <button type="button" onClick={loadContext} className="min-h-11 rounded-xl border border-stone-300 px-4 py-2 font-medium dark:border-white/20">
            Retry
          </button>
        </div>
      </CenteredState>
    );
  }

  return (
    <RiderPortalContext.Provider value={providerValue}>
      <Outlet key={`${explicitBusinessId}:${auth.user?.id || ""}`} />
    </RiderPortalContext.Provider>
  );
}
